// TEMPORARY: per-tab wiring of the shared publisher for the Vue 2 app.
// Lives for the whole tab (module scope), so it survives SPA route changes
// such as Detail -> Pay, which is what lets the customer keep seeing the QR
// while staff take payment.

import { createDisplayPublisher } from '@shared/customer-display/publisher';
import { createTransport } from '@shared/customer-display/transport';

export const DOCUMENT_VIEW_OWNER = 'vue2-document-session';
const RELEASE_GRACE_MS = 600;

let publisher = null;
let openPages = 0;
let releaseTimer = null;

// HMR safety: this module has no .vue boundary of its own, so webpack's Hot
// Module Replacement re-executes this module's top-level code (resetting
// `publisher` to null) whenever a dev-time edit to anything it depends on
// (publisher.js, transport.js, or anything documentMixin.js/paymentMixin.js
// pull in) cannot be accepted here and bubbles up to whichever Detail/Pay
// page ends up self-accepting the update. Without this, the OLD publisher's
// BroadcastChannel/visibilitychange/focus listeners and status-check timer
// are orphaned (nothing in production code ever calls its own destroy()) --
// it keeps running, now permanently stuck on whatever view it last had,
// while document pages move on to a brand new publisher that starts from
// view = null. Depending on which instance answers the Customer Display's
// next HELLO, the display can get stuck replaying that stale old view --
// exactly the "frozen after HMR" bug this guards against.
//
// __captureHmrState/__restoreHmrState are the actual handoff logic, kept as
// plain exported functions (rather than inline in the module.hot glue below)
// purely so a test can exercise them directly -- there is no real webpack
// HMR runtime inside Jest to simulate module.hot against. The real
// module.hot.dispose() hands this module's live state to whatever replaces
// it; the replacement picks it back up below instead of starting over, so
// the SAME publisher object (same listeners, same in-memory view) survives
// the swap untouched. module.hot is undefined outside webpack-dev-server
// (including every production build), so that wiring is a no-op there.
export function __captureHmrState() {
  return { publisher, openPages };
}

export function __restoreHmrState(data) {
  if (!data) return;
  publisher = data.publisher || null;
  openPages = data.openPages || 0;
  // A timer id from the disposed module instance cannot be reused here
  // (timers are not transferable across module re-executions) -- any
  // pending release simply gets re-evaluated by the next enter/leave call
  // instead of carrying a dangling clearTimeout target forward.
}

if (typeof module !== 'undefined' && module.hot) {
  __restoreHmrState(module.hot.data);
  module.hot.dispose((data) => {
    Object.assign(data, __captureHmrState());
  });
}

// The Customer Display lives in this same Vue 2 app
// (src/views/pages/customer-display/CustomerDisplayPage.vue, route added in
// src/router/index.js). The separate Vue 3 app that used to serve it at
// /next/customer-display has been retired and removed entirely.
export function displayUrl() {
  return '/customer-display';
}

export function getPublisher() {
  if (!publisher) {
    publisher = createDisplayPublisher({ transport: createTransport(), displayUrl: displayUrl() });
  }
  return publisher;
}

// A Detail/Pay page mounted. Reference counting (rather than a single
// timer reset) keeps the display alive across Detail -> Pay, where vue-router
// creates the new page before destroying the old one.
export function enterDocumentPage() {
  openPages += 1;
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
}

// The last document page of this tab went away without paying: return the
// display to idle. A THANK YOU is never cleared by this.
export function leaveDocumentPage(branch) {
  openPages = Math.max(0, openPages - 1);
  if (openPages > 0) return;
  if (releaseTimer) clearTimeout(releaseTimer);
  releaseTimer = setTimeout(() => {
    releaseTimer = null;
    if (openPages === 0) getPublisher().release(DOCUMENT_VIEW_OWNER, branch);
  }, RELEASE_GRACE_MS);
}
